import React, { useState, useEffect } from 'react';

// Grant-Related Documents dashboard.
//
// Folders ("groups") come from three places:
//   1. A built-in "Common Documents" folder for paperwork that applies to every
//      grant — Research Integrity Assurance and the like.
//   2. One folder per grant on the company's grant list, so a folder appears on
//      its own once a new grant is recorded in Grant & Fund Management.
//   3. Folders the client adds here directly, for grants not yet in that list.
//
// Within a folder are collapsible sections, and within each section are
// categories that hold uploaded files. Documents that are refreshed every year
// (progress reports, the integrity assurance) use the year as the category, so
// each year's filing is kept as its own record.

const GRANT_GROUP_PREFIX = 'grant::';
const COMMON_GROUP_KEY = 'commonDocuments';

// Sections that every grant folder starts with.
const GRANT_SECTIONS = [
  {
    key: 'progressReports',
    title: 'Progress Reports',
    description: 'One folder per reporting year.',
    isYearly: true
  }
];

// Sections in the built-in Common Documents folder.
const COMMON_SECTIONS = [
  {
    key: 'researchIntegrityAssurance',
    title: 'Research Integrity Assurance',
    description: 'Renewed every year — keep each year’s filing as its own record.',
    isYearly: true
  },
  {
    key: 'otherCommonDocuments',
    title: 'Other Common Documents',
    description: 'Policies and assurances that apply across all grants.',
    categories: []
  }
];

const emptyStructure = { groups: [], sections: [], categories: [], hidden: { groups: [], sections: [], categories: [] } };
const uid = () => `g-${Math.random().toString(36).slice(2)}`;
const sectionComposite = (groupKey, sectionKey) => `${groupKey}::${sectionKey}`;
const categoryComposite = (groupKey, sectionKey, name) => `${groupKey}::${sectionKey}::${name}`;

const currentYear = () => new Date().getFullYear();

// A year category sorts newest-first; anything else keeps its given order.
const sortCategories = (categories) => {
  const years = categories.filter(c => /^\d{4}$/.test(c.name));
  const rest = categories.filter(c => !/^\d{4}$/.test(c.name));
  years.sort((a, b) => Number(b.name) - Number(a.name));
  return [...years, ...rest];
};

const GrantRelatedDocuments = () => {
  const [files, setFiles] = useState([]);
  const [grants, setGrants] = useState([]);
  const [structure, setStructure] = useState(emptyStructure);
  const [fileUploadLimit] = useState(25 * 1024 * 1024); // 25MB

  const [activeGroupKey, setActiveGroupKey] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFilePath, setPreviewFilePath] = useState(null);

  // Modal state for adding a folder / section / category
  const [modal, setModal] = useState(null); // { type, groupKey?, sectionKey?, isYearly? }
  const [modalName, setModalName] = useState('');

  const API = process.env.REACT_APP_BACKEND_API_URL;

  // ---- Data loading ----
  const fetchFiles = async () => {
    try {
      const response = await fetch(`${API}/grant-documents`, { method: 'GET', credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      setFiles(await response.json());
    } catch (error) {
      console.error('Error fetching grant documents:', error);
    }
  };

  const fetchStructure = async () => {
    try {
      const response = await fetch(`${API}/grant-documents/structure`, { method: 'GET', credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      setStructure({
        groups: data.groups || [],
        sections: data.sections || [],
        categories: data.categories || [],
        hidden: {
          groups: data.hidden?.groups || [],
          sections: data.hidden?.sections || [],
          categories: data.hidden?.categories || []
        }
      });
    } catch (error) {
      console.error('Error fetching grant document structure:', error);
    }
  };

  // Grants recorded in Grant & Fund Management become folders automatically.
  const fetchGrants = async () => {
    try {
      const response = await fetch(`${API}/grants`, { method: 'GET', credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      setGrants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching grants:', error);
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchStructure();
    fetchGrants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistStructure = async (next) => {
    setStructure(next);
    try {
      const response = await fetch(`${API}/grant-documents/structure`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next)
      });
      if (!response.ok) alert('Failed to save changes');
    } catch (error) {
      console.error('Error saving grant document structure:', error);
      alert('Failed to save changes');
    }
  };

  // ---- Derived structure ----
  const grantGroupKey = (grant) => `${GRANT_GROUP_PREFIX}${grant['Grant/Contract Number']}`;

  const grantTitle = (grant) => {
    const number = grant['Grant/Contract Number'];
    const nickname = grant['Grant Nickname'];
    const projectTitle = grant['Project Title'];
    const label = nickname || projectTitle;
    return label ? `${label} (${number})` : `Grant ${number}`;
  };

  const getGroups = () => {
    const common = structure.hidden.groups.includes(COMMON_GROUP_KEY)
      ? []
      : [{ key: COMMON_GROUP_KEY, title: 'Common Documents', subtitle: 'Applies to all grants', isCustom: false, isGrant: false }];

    const grantGroups = grants
      .filter(g => g['Grant/Contract Number'])
      .map(g => ({
        key: grantGroupKey(g),
        title: grantTitle(g),
        subtitle: g['Principal Investigator'] ? `PI: ${g['Principal Investigator']}` : 'Grant',
        isCustom: false,
        isGrant: true
      }))
      .filter(g => !structure.hidden.groups.includes(g.key));

    const customs = structure.groups
      .filter(g => !structure.hidden.groups.includes(g.id))
      .map(g => ({ key: g.id, title: g.title, subtitle: 'Grant', isCustom: true, isGrant: true }));

    return [...common, ...grantGroups, ...customs];
  };

  // Built-in sections depend on whether the folder is Common Documents or a grant.
  const builtinSectionsFor = (groupKey) =>
    groupKey === COMMON_GROUP_KEY ? COMMON_SECTIONS : GRANT_SECTIONS;

  const getSections = (groupKey) => {
    const builtIns = builtinSectionsFor(groupKey)
      .filter(s => !structure.hidden.sections.includes(sectionComposite(groupKey, s.key)))
      .map(s => ({ key: s.key, title: s.title, description: s.description, isYearly: !!s.isYearly, isCustom: false }));
    const customs = structure.sections
      .filter(s => s.groupKey === groupKey)
      .map(s => ({ key: s.id, title: s.title, description: '', isYearly: false, isCustom: true }));
    return [...builtIns, ...customs];
  };

  const getCategories = (groupKey, sectionKey) => {
    const builtinSection = builtinSectionsFor(groupKey).find(s => s.key === sectionKey);

    // Yearly sections start with the current year so there is always somewhere to
    // upload; earlier years are added on demand and persisted in the structure.
    const seeded = builtinSection?.isYearly
      ? [String(currentYear())]
      : (builtinSection?.categories || []);

    const builtIns = seeded
      .filter(name => !structure.hidden.categories.includes(categoryComposite(groupKey, sectionKey, name)))
      .map(name => ({ name, isCustom: false }));

    const customs = structure.categories
      .filter(c => c.groupKey === groupKey && c.sectionKey === sectionKey)
      // A seeded year may also have been added by hand; don't show it twice.
      .filter(c => !builtIns.some(b => b.name === c.name))
      .map(c => ({ name: c.name, isCustom: true, categoryId: c.id }));

    return sortCategories([...builtIns, ...customs]);
  };

  const getFilesByCategory = (groupKey, sectionKey, category) =>
    files.filter(f => f.groupKey === groupKey && f.sectionKey === sectionKey && f.documentType === category);

  const fileCountForGroup = (groupKey) => files.filter(f => f.groupKey === groupKey).length;

  // ---- Add handlers ----
  const openAdd = (type, groupKey, sectionKey, isYearly) => {
    setModal({ type, groupKey, sectionKey, isYearly });
    // Adding a year usually means last year's filing, so offer that as the default.
    setModalName(type === 'category' && isYearly ? String(currentYear() - 1) : '');
  };

  const submitAdd = async () => {
    const name = modalName.trim();
    if (!name) { alert('Name is required'); return; }

    if (modal.type === 'group') {
      await persistStructure({ ...structure, groups: [...structure.groups, { id: uid(), title: name }] });
    } else if (modal.type === 'section') {
      await persistStructure({
        ...structure,
        sections: [...structure.sections, { id: uid(), groupKey: modal.groupKey, title: name }]
      });
    } else if (modal.type === 'category') {
      if (modal.isYearly && !/^\d{4}$/.test(name)) {
        alert('Enter a four-digit year, e.g. 2025');
        return;
      }
      const exists = getCategories(modal.groupKey, modal.sectionKey)
        .some(c => c.name.toLowerCase() === name.toLowerCase());
      if (exists) { alert('That entry already exists in this section'); return; }
      await persistStructure({
        ...structure,
        categories: [...structure.categories, { id: uid(), groupKey: modal.groupKey, sectionKey: modal.sectionKey, name }]
      });
    }

    setModal(null);
    setModalName('');
  };

  // ---- Delete handlers ----
  const deleteGroup = async (group) => {
    if (!window.confirm(`Remove the "${group.title}" folder from this page? Uploaded files are not deleted.`)) return;
    if (group.isCustom) {
      await persistStructure({
        ...structure,
        groups: structure.groups.filter(g => g.id !== group.key),
        sections: structure.sections.filter(s => s.groupKey !== group.key),
        categories: structure.categories.filter(c => c.groupKey !== group.key)
      });
    } else {
      await persistStructure({
        ...structure,
        hidden: { ...structure.hidden, groups: [...structure.hidden.groups, group.key] }
      });
    }
    setActiveGroupKey(null);
  };

  const deleteSection = async (groupKey, section) => {
    if (!window.confirm(`Delete the "${section.title}" section? Uploaded files are not deleted.`)) return;
    if (section.isCustom) {
      await persistStructure({
        ...structure,
        sections: structure.sections.filter(s => s.id !== section.key),
        categories: structure.categories.filter(c => !(c.groupKey === groupKey && c.sectionKey === section.key))
      });
    } else {
      await persistStructure({
        ...structure,
        hidden: { ...structure.hidden, sections: [...structure.hidden.sections, sectionComposite(groupKey, section.key)] }
      });
    }
  };

  const deleteCategory = async (groupKey, sectionKey, category) => {
    if (!window.confirm(`Delete "${category.name}"? Uploaded files are not deleted.`)) return;
    if (category.isCustom) {
      await persistStructure({
        ...structure,
        categories: structure.categories.filter(c => c.id !== category.categoryId)
      });
    } else {
      await persistStructure({
        ...structure,
        hidden: {
          ...structure.hidden,
          categories: [...structure.hidden.categories, categoryComposite(groupKey, sectionKey, category.name)]
        }
      });
    }
  };

  // ---- File handlers ----
  const handleUpload = async (groupKey, sectionKey, category, file) => {
    if (!file) return;
    if (file.size > fileUploadLimit) {
      alert(`File size exceeds the limit of ${fileUploadLimit / (1024 * 1024)} MB.`);
      return;
    }

    try {
      // Keys can contain characters that are awkward in an object path.
      const safe = (value) => String(value).replace(/[^a-zA-Z0-9-_. ]/g, '_');
      const filePath = `${safe(groupKey)}/${safe(sectionKey)}/${safe(category)}/${Date.now()}_${safe(file.name)}`;
      const contentType = file.type || 'application/octet-stream';

      const urlResponse = await fetch(`${API}/grant-documents/upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filePath, contentType })
      });
      if (!urlResponse.ok) throw new Error(await urlResponse.text());
      const { url } = await urlResponse.json();

      const uploadResponse = await fetch(url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!uploadResponse.ok) throw new Error('Failed to upload file to storage');

      const confirmResponse = await fetch(`${API}/grant-documents/confirm-upload`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filePath,
          groupKey,
          sectionKey,
          category,
          originalName: file.name,
          contentType
        })
      });
      if (!confirmResponse.ok) throw new Error('File uploaded but its details could not be saved');

      await fetchFiles();
    } catch (error) {
      console.error('Error uploading grant document:', error);
      alert('Error uploading file: ' + error.message);
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await fetch(`${API}/grant-documents/download-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filePath: file.filePath, originalName: file.originalName })
      });
      if (!response.ok) throw new Error(await response.text());
      const { url } = await response.json();

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalName || file.filePath.split('/').pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading grant document:', error);
      alert('Error downloading file: ' + error.message);
    }
  };

  const handlePreview = async (file) => {
    try {
      const response = await fetch(`${API}/grant-documents/preview-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filePath: file.filePath })
      });
      if (!response.ok) throw new Error(await response.text());
      const { url } = await response.json();
      setPreviewUrl(url);
      setPreviewFilePath(file.filePath);
    } catch (error) {
      console.error('Error previewing grant document:', error);
      alert('Error previewing file.');
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.originalName}"? This cannot be undone.`)) return;
    try {
      const response = await fetch(`${API}/grant-documents`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filePath: file.filePath })
      });
      if (!response.ok) throw new Error(await response.text());
      if (previewFilePath === file.filePath) {
        setPreviewUrl('');
        setPreviewFilePath(null);
      }
      await fetchFiles();
    } catch (error) {
      console.error('Error deleting grant document:', error);
      alert('Error deleting file: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const renderFilesTable = (categoryFiles) => (
    <div className="border-t border-gray-200 overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">File</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody>
          {categoryFiles.map((file) => (
            <tr key={file.filePath}>
              <td className="px-4 py-3 text-sm text-gray-900">{file.originalName}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{formatDate(file.lastModified)}</td>
              <td className="px-4 py-3 text-center">
                <div className="flex items-center justify-center gap-3">
                  <button onClick={() => handlePreview(file)} className="text-blue-600 hover:text-blue-700 text-sm">View</button>
                  <button onClick={() => handleDownload(file)} className="text-blue-600 hover:text-blue-700 text-sm">Download</button>
                  <button onClick={() => handleDelete(file)} className="text-red-600 hover:text-red-700 text-sm">Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderPreview = (categoryFiles) => (
    previewUrl && previewFilePath && categoryFiles.some(f => f.filePath === previewFilePath) && (
      <div className="bg-white rounded-lg shadow-sm border border-blue-200 p-6 mt-3">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
          <button
            onClick={() => { setPreviewUrl(''); setPreviewFilePath(null); }}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {['jpg', 'jpeg', 'png', 'gif'].includes(previewFilePath.split('.').pop()?.toLowerCase()) ? (
          <img src={previewUrl} alt="Document Preview" className="max-w-full max-h-[500px] mx-auto" />
        ) : (
          <iframe src={previewUrl} title="Document Preview" className="w-full h-[500px] border border-gray-200 rounded" />
        )}
      </div>
    )
  );

  const renderAddModal = () => {
    if (!modal) return null;
    const titles = { group: 'Add Grant Folder', section: 'Add Section', category: modal.isYearly ? 'Add Year' : 'Add Category' };
    const labels = { group: 'Grant Name or Number', section: 'Section Name', category: modal.isYearly ? 'Year' : 'Category Name' };
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{titles[modal.type]}</h2>
          {modal.type === 'group' && (
            <p className="text-sm text-red-600 mb-4">
              Only add a grant manually if it is not listed within the Grant Submissions portal.
              All grants marked as funded in the Grant Submissions Portal will have a folder
              created automatically.
            </p>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{labels[modal.type]} *</label>
              <input
                type="text"
                value={modalName}
                onChange={(e) => setModalName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                autoFocus
              />
            </div>
            <div className="flex space-x-2 pt-2">
              <button onClick={submitAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-1">
                Create
              </button>
              <button
                onClick={() => { setModal(null); setModalName(''); }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const groups = getGroups();
  const activeGroup = activeGroupKey ? groups.find(g => g.key === activeGroupKey) : null;

  // ---- Dashboard (folder grid) ----
  if (!activeGroup) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Grant-Related Documents</h1>
          <p className="text-gray-600 mt-2">
            Progress Reports, Research Integrity Assurance, and other paperwork tied to your grants.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(group => (
            <div
              key={group.key}
              onClick={() => { setActiveGroupKey(group.key); setPreviewUrl(''); setPreviewFilePath(null); }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all relative group"
            >
              <button
                onClick={(e) => { e.stopPropagation(); deleteGroup(group); }}
                className="absolute top-2 right-2 text-gray-300 hover:text-red-500 text-sm opacity-0 group-hover:opacity-100"
                title="Remove folder"
              >
                ✕
              </button>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                group.isGrant ? 'bg-teal-100' : 'bg-blue-100'
              }`}>
                <svg
                  className={`w-6 h-6 ${group.isGrant ? 'text-teal-600' : 'text-blue-600'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{group.title}</h3>
              <p className="text-xs text-gray-400 mt-1">{group.subtitle}</p>
              <p className="text-sm text-gray-500 mt-1">{fileCountForGroup(group.key)} file(s)</p>
            </div>
          ))}

          <button
            onClick={() => openAdd('group')}
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-all"
          >
            <span className="text-3xl leading-none">+</span>
            <span className="mt-2 text-sm font-medium">Add Grant Folder</span>
          </button>
        </div>

        {renderAddModal()}
      </div>
    );
  }

  // ---- Folder detail view ----
  const sections = getSections(activeGroup.key);
  return (
    <div className="max-w-6xl mx-auto p-6">
      <button
        onClick={() => { setActiveGroupKey(null); setPreviewUrl(''); setPreviewFilePath(null); }}
        className="text-blue-600 hover:text-blue-700 text-sm mb-4 flex items-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Grant-Related Documents
      </button>

      <div className="mb-6 flex justify-between items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{activeGroup.title}</h1>
          <p className="text-gray-500 text-sm mt-1">{activeGroup.subtitle}</p>
        </div>
        <button
          onClick={() => openAdd('section', activeGroup.key)}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap"
        >
          + Add Section
        </button>
      </div>

      {sections.length === 0 && (
        <p className="text-gray-500 mb-6">No sections yet. Click "+ Add Section" to create one.</p>
      )}

      {sections.map(section => {
        const compositeKey = sectionComposite(activeGroup.key, section.key);
        const isCollapsed = !expandedSections[compositeKey];
        const categories = getCategories(activeGroup.key, section.key);
        return (
          <div key={section.key} className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div
              className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleSection(compositeKey)}
            >
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-sm">{isCollapsed ? '▶' : '▼'}</span>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
                  {section.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>
                  )}
                </div>
                <span className="text-xs text-gray-400">
                  {categories.length} {section.isYearly ? (categories.length === 1 ? 'year' : 'years') : (categories.length === 1 ? 'category' : 'categories')}
                </span>
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => openAdd('category', activeGroup.key, section.key, section.isYearly)}
                  className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 whitespace-nowrap"
                >
                  {section.isYearly ? '+ Add Year' : '+ Add Category'}
                </button>
                <button
                  onClick={() => deleteSection(activeGroup.key, section)}
                  className="px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                  title="Delete section"
                >
                  Delete
                </button>
              </div>
            </div>

            {!isCollapsed && (
              <div className="border-t border-gray-200 p-4 space-y-3">
                {categories.length === 0 && (
                  <p className="text-gray-500 text-sm">
                    Nothing here yet. Click "{section.isYearly ? '+ Add Year' : '+ Add Category'}".
                  </p>
                )}
                {categories.map(cat => {
                  const categoryFiles = getFilesByCategory(activeGroup.key, section.key, cat.name);
                  return (
                    <div key={cat.name} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                      <div className="flex justify-between items-center p-3 gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                          <h3 className="font-medium text-gray-900">{cat.name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            categoryFiles.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {categoryFiles.length > 0
                              ? `${categoryFiles.length} file${categoryFiles.length === 1 ? '' : 's'}`
                              : 'Empty'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                            Upload
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleUpload(activeGroup.key, section.key, cat.name, e.target.files[0]);
                                  e.target.value = '';
                                }
                              }}
                            />
                          </label>
                          <button
                            onClick={() => deleteCategory(activeGroup.key, section.key, cat)}
                            className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                            title="Delete"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      {categoryFiles.length > 0 && renderFilesTable(categoryFiles)}
                      {renderPreview(categoryFiles)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {renderAddModal()}
    </div>
  );
};

export default GrantRelatedDocuments;
